<template>
    <v-dialog fullscreen v-model="dialog" transition="dialog-bottom-transition">
        <v-card tile flat>
            <v-app-bar dark flat fixed>
                <v-row align="center" no-gutters>
                    <v-col cols="10" class="app-title text-truncate">
                        {{ title }}
                    </v-col>
                    <v-col cols="2" align="right">
                        <v-btn v-if="backButton" color="primary" icon @click="hide">
                            <v-icon>$close</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </v-app-bar>

            <v-sheet :height="isMobile ? 64 : 84" color="transparent" />

            <v-card-text>
                <v-row>
                    <v-col cols="12" md="7" class="pa-0">
                        <v-carousel cycle :height="isMobile ? '150px' : '100%'" hide-delimiters :show-arrows="false"
                            touchless>
                            <v-carousel-item v-for="(image, i) in images" :key="i" :src="image" />
                        </v-carousel>
                    </v-col>
                    <v-col cols="12" md="5">
                        <loading-spinner v-if="loading" />
                        <summary-data :class="[{ 'mt-3': !isMobile }]" v-else :summary="summary" @back="goToRoutines"
                            :is-competence="isCompetence" />
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import { db } from "@/utils/Firebase";
import SummaryData from "@/components/student/SummaryData";
import LoadingSpinner from "@/components/common/LoadingSpinner";

export default {
    name: "SummaryAssignedRoutineDialog",
    components: { LoadingSpinner, SummaryData },
    props: {
        assignedRoutineId: {
            type: String,
            default: '',
        },
        backButton: {
            type: Boolean,
            default: false,
        },
        isCoachWatch: {
            type: Boolean,
            default: false,
        },
        isCompetence: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            dialog: false,
            loading: false,
            canExit: false,
            title: '',
            summary: null,
            RpeTable: [
                { name: 10, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 9.5, reps: [97, 93, 89, 86, 84, 82, 80, 77.5] },
                { name: 9, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 8.5, reps: [93, 89, 86, 84, 82, 80, 77.5, 74.5] },
                { name: 8, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 7.5, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 7, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
            RirTable: [
                { name: 0, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 1, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 2, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 3, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 4, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ]
        }
    },
    computed: {
        ...mapState(['dialogActive']),
        images() {
            return [
                require('@/assets/img/post_train_desktop.png'),
                require('@/assets/img/post_train_desktop.png'),
                require('@/assets/img/post_train_desktop.png')
            ]
        }
    },
    methods: {
        ...mapMutations(['setDialogActive', 'setTitle']),
        show() {
            const vm = this
            vm.loadSummaryData()
            vm.dialog = true
            vm.setDialogActive(true)
        },
        hide() {
            const vm = this
            if (vm.canExit || vm.backButton) {
                vm.dialog = false
                vm.setDialogActive(false)
                vm.$emit('hide')
            }
        },
        goToRoutines() {
            const vm = this
            vm.setDialogActive(false)
            vm.canExit = true
            vm.setTitle('')
            let routeName = 'student.routines'
            if (vm.isCoachWatch) routeName = `${vm.accountType}.calendar`
            vm.$router.push({ name: routeName })
        },
        async loadSummaryData() {
            const vm = this
            vm.loading = true
            let assignedRoutineRef = await db.collection('assignedRoutines').doc(vm.assignedRoutineId).get()
            let assignedRoutine = assignedRoutineRef.data()

            // CURRENT ROUTINE NAME
            if (!assignedRoutine.name && !assignedRoutine.versus) {
                let routineRef = await db.collection('routines').doc(vm.assignedRoutine.routineId).get()
                vm.title = routineRef.data().name
            } else vm.title = assignedRoutine.name || assignedRoutine.versus

            let summary = {}

            // COMMENT
            summary.comment = assignedRoutine.advance.postInfo && assignedRoutine.advance.postInfo.comment || null

            // CURRENT DURATION
            summary.duration = assignedRoutine.advance.postInfo && assignedRoutine.advance.postInfo.duration || 0
            summary.effort = assignedRoutine.advance.postInfo && assignedRoutine.advance.postInfo.effort || 0

            // CHARGE
            summary.charge = summary.duration * summary.effort

            // WELLNESS
            let preInfo = assignedRoutine.advance.preInfo
            summary.wellness = (preInfo.muscles_hurt + preInfo.sleep_night + preInfo.stressed + preInfo.tired) / 4

            // CHANGES
            summary.changes = assignedRoutine?.advance?.changes || []

            // REPS PER EXERCISE
            if (!vm.isCompetence) {
                let reps = 0
                let exercises = 0
                let sets = 0
                assignedRoutine.blocks.forEach(block => {
                    if (block.isCircuit) {
                        sets += block.sets || 0
                    }
                    block.exercises.forEach(exercise => {
                        if (exercise.type !== 'rest') {
                            exercises++
                            if (block.isCircuit && exercise.config.type === 'reps') {
                                reps += Number(exercise.config.value || 0)
                            } else {
                                exercise.config?.sets?.forEach(set => {
                                    sets++
                                    let currentReps = set.data.find(data => data.name === vm.$t('exercises.reps'))
                                    reps += currentReps && Number(currentReps.value) || 0
                                })
                            }
                        }
                    })
                })

                summary.reps = reps
                summary.exercises = exercises
                summary.sets = sets
            }

            vm.summary = summary
            vm.loading = false
        },
        getValueFromTable(variable, value, set) {
            const vm = this
            if (variable === vm.$t('exercises.rpe')) {
                if (value > 10) value = 10
                if (value < 7) value = 7
            }
            if (variable === vm.$t('exercises.rir')) {
                if (value > 4) value = 4
                if (value < 0) value = 0
            }
            let findValueTable = vm[`${variable}Table`]?.find(table => table.name === value) || null
            let reps = set.data.find(data => data.name === vm.$t(`exercises.reps`)) || null
            let repsValue = reps && Number(reps.value || 0) - 1 || 0
            if (repsValue > 7) repsValue = 7
            if (repsValue < 0) repsValue = 0
            return findValueTable && reps && findValueTable.reps[repsValue] || 0
        }
    },
    watch: {
        dialogActive(val) {
            if (!val) this.hide()
        },
    },
    beforeRouteLeave(to, from, next) {
        const vm = this
        if (vm.canExit) return next()
        return next(false)
    }
}
</script>

<style scoped>
.app-title {
    font-size: 20px;
}
</style>